// File validation utilities for secure file uploads
// Matches server-side validation in cpfgs-api S3Service

/**
 * Allowed file types with their MIME types
 * Matches server-side ALLOWED_CONTENT_TYPES in S3Service.java
 */
export const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  // Images
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.png': ['image/png'],
  // '.gif': ['image/gif'],
  // '.webp': ['image/webp'],
  // Documents
  // '.pdf': ['application/pdf'],
  // Spreadsheets
  // '.csv': ['text/csv', 'application/csv', 'text/plain'],
  // '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  // '.xls': ['application/vnd.ms-excel'],
}

/**
 * Image magic bytes for validation
 * Matches server-side validateImageMagicBytes in S3Service.java
 */
const IMAGE_MAGIC_BYTES: Record<string, number[]> = {
  'image/png': [0x89, 0x50, 0x4E, 0x47],  // PNG: 89 50 4E 47
  'image/jpeg': [0xFF, 0xD8, 0xFF],        // JPEG: FF D8 FF
  'image/gif': [0x47, 0x49, 0x46, 0x38],   // GIF: 47 49 46 38 (GIF8)
}

/**
 * Maximum file size in bytes (10MB)
 * Matches server-side MAX_FILE_SIZE in S3Service.java
 */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

/**
 * Maximum file size for Rich Menu images (1MB - LINE requirement)
 */
export const MAX_RICH_MENU_IMAGE_SIZE_BYTES = 1 * 1024 * 1024

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean
  error?: string
}

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase()
  return ext ? `.${ext}` : ''
}

/**
 * Check if file extension is allowed
 */
export const isAllowedExtension = (filename: string, allowedExtensions?: string[]): boolean => {
  const ext = getFileExtension(filename)
  if (allowedExtensions) {
    return allowedExtensions.some(allowed => allowed.toLowerCase() === ext)
  }
  return ext in ALLOWED_FILE_TYPES
}

/**
 * Check if MIME type matches the file extension
 */
export const isMimeTypeValid = (filename: string, mimeType: string): boolean => {
  const ext = getFileExtension(filename)
  const allowedMimeTypes = ALLOWED_FILE_TYPES[ext]

  if (!allowedMimeTypes) {
    return false
  }

  return allowedMimeTypes.includes(mimeType)
}

/**
 * Validate image magic bytes to prevent file type spoofing
 * Returns a Promise that resolves to true if valid
 */
export const validateImageMagicBytes = async (file: File): Promise<boolean> => {
  // Only validate images
  if (!file.type.startsWith('image/')) {
    return true
  }

  const expectedBytes = IMAGE_MAGIC_BYTES[file.type]
  if (!expectedBytes) {
    // No magic bytes defined for this type, allow
    return true
  }

  try {
    const buffer = await file.slice(0, expectedBytes.length).arrayBuffer()
    const bytes = new Uint8Array(buffer)

    for (let i = 0; i < expectedBytes.length; i++) {
      if (bytes[i] !== expectedBytes[i]) {
        return false
      }
    }

    return true
  } catch {
    return false
  }
}

/**
 * Validate file size
 */
export const validateFileSize = (file: File, maxSizeBytes: number): FileValidationResult => {
  if (file.size > maxSizeBytes) {
    const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024))
    return {
      valid: false,
      error: `ไฟล์มีขนาดใหญ่เกินไป (สูงสุด ${maxSizeMB}MB)`
    }
  }
  return { valid: true }
}

/**
 * Validate file extension
 */
export const validateFileExtension = (file: File, allowedExtensions?: string[]): FileValidationResult => {
  if (!isAllowedExtension(file.name, allowedExtensions)) {
    const allowed = allowedExtensions || Object.keys(ALLOWED_FILE_TYPES)
    return {
      valid: false,
      error: `ประเภทไฟล์ไม่ถูกต้อง รองรับเฉพาะ: ${allowed.join(', ')}`
    }
  }
  return { valid: true }
}

/**
 * Validate file MIME type matches extension
 */
export const validateFileMimeType = (file: File): FileValidationResult => {
  if (!isMimeTypeValid(file.name, file.type)) {
    return {
      valid: false,
      error: 'ประเภทไฟล์ไม่ตรงกับนามสกุลไฟล์'
    }
  }
  return { valid: true }
}

/**
 * Comprehensive file validation
 * Validates size, extension, MIME type, and optionally magic bytes
 */
export const validateFile = async (
  file: File,
  options: {
    maxSizeBytes?: number
    allowedExtensions?: string[]
    validateMagicBytes?: boolean
  } = {}
): Promise<FileValidationResult> => {
  const {
    maxSizeBytes = MAX_FILE_SIZE_BYTES,
    allowedExtensions,
    validateMagicBytes = true
  } = options

  // Validate file size
  const sizeResult = validateFileSize(file, maxSizeBytes)
  if (!sizeResult.valid) {
    return sizeResult
  }

  // Validate file extension
  const extResult = validateFileExtension(file, allowedExtensions)
  if (!extResult.valid) {
    return extResult
  }

  // Validate MIME type
  const mimeResult = validateFileMimeType(file)
  if (!mimeResult.valid) {
    return mimeResult
  }

  // Validate magic bytes for images
  if (validateMagicBytes && file.type.startsWith('image/')) {
    const magicBytesValid = await validateImageMagicBytes(file)
    if (!magicBytesValid) {
      return {
        valid: false,
        error: 'ไฟล์รูปภาพไม่ถูกต้องหรือเสียหาย'
      }
    }
  }

  return { valid: true }
}

/**
 * Validate Rich Menu image specifically
 * - Max 1MB (LINE requirement)
 * - Only JPG, JPEG, PNG
 */
export const validateRichMenuImage = async (file: File): Promise<FileValidationResult> => {
  return validateFile(file, {
    maxSizeBytes: MAX_RICH_MENU_IMAGE_SIZE_BYTES,
    allowedExtensions: ['.jpg', '.jpeg', '.png'],
    validateMagicBytes: true
  })
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}
